import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { BrutalCard, BrutalButton } from "../components/Brutal";
import { Package, Receipt, QrCode, MagnifyingGlass } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

function CodeEntry({ initial, error }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [value, setValue] = useState(initial || "");
  return (
    <div className="max-w-sm mx-auto pt-10" data-testid="portal-code-entry">
      <BrutalCard className="p-6 space-y-4 text-center">
        <QrCode size={36} weight="bold" className="mx-auto text-[#8a6a1c]" />
        <div>
          <h1 className="text-xl font-bold tracking-tight text-zinc-950">{t("portal.title")}</h1>
          <p className="text-sm text-zinc-500 mt-1">{t("portal.enter_code_hint")}</p>
        </div>
        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="portal-error">
            {t("portal.not_found")}
          </p>
        )}
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (value.trim()) navigate(`/portal/${value.trim().toUpperCase()}`);
          }}
        >
          <input
            value={value}
            onChange={(e) => setValue(e.target.value.toUpperCase())}
            placeholder="CG-01"
            className="flex-1 border border-zinc-300 rounded-lg px-3 py-2 font-mono tracking-widest text-center uppercase focus:outline-none focus:ring-2 focus:ring-[#c9a24a]"
            data-testid="portal-code-input"
          />
          <BrutalButton type="submit" size="sm" data-testid="portal-code-submit">
            <MagnifyingGlass size={14} weight="bold" /> {t("portal.view")}
          </BrutalButton>
        </form>
      </BrutalCard>
    </div>
  );
}

export default function PortalPage() {
  const { t } = useTranslation();
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!code) return;
    setData(null);
    setError(false);
    axios.get(`${API}/api/portal/${encodeURIComponent(code)}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [code]);

  if (!code || error) return <CodeEntry initial={code} error={error} />;
  if (data === null) return <div className="p-8 text-center text-sm text-zinc-500">{t("common.loading")}</div>;

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="portal-page">
      {/* Header card */}
      <BrutalCard className="p-6 text-center space-y-2 border-[#c9a24a]">
        <p className="text-[11px] font-mono font-semibold tracking-[0.25em] text-[#8a6a1c] uppercase">
          {t("portal.title")}
        </p>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950" data-testid="portal-name">
          {data.name}
        </h1>
        <p className="inline-block text-lg font-bold font-mono tracking-widest px-3 py-1 bg-[#fbf6e7] text-[#8a6a1c] border-2 border-[#c9a24a] rounded-lg" data-testid="portal-code-badge">
          {data.code}
        </p>
      </BrutalCard>

      {/* Open balance */}
      <BrutalCard className="p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50/60 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Receipt size={16} weight="bold" className="text-[#8a6a1c]" />
            <h2 className="text-sm font-semibold tracking-wide uppercase text-zinc-700">{t("portal.balance")}</h2>
          </div>
          <span
            className={`text-lg font-bold font-mono ${data.open_total > 0 ? "text-[#8a6a1c]" : "text-emerald-700"}`}
            data-testid="portal-open-total"
          >
            {money(data.open_total)}
          </span>
        </div>
        {data.open_items.length === 0 ? (
          <p className="px-5 py-4 text-sm text-emerald-700" data-testid="portal-balance-clear">
            {t("portal.all_settled")}
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="portal-open-items">
            <tbody>
              {data.open_items.map((it, i) => (
                <tr key={i} className="border-b border-zinc-50 last:border-0">
                  <td className="px-5 py-2.5 font-medium text-zinc-900">{it.product_name}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-zinc-600 whitespace-nowrap">
                    {it.quantity} × {money(it.unit_price)}
                  </td>
                  <td className="px-5 py-2.5 text-right font-mono font-semibold text-zinc-900 whitespace-nowrap">
                    {money(it.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data.open_total > 0 && (
          <p className="px-5 py-3 text-xs text-zinc-500 border-t border-zinc-100 bg-zinc-50/40">
            {t("portal.etransfer_note", { code: data.code })}
          </p>
        )}
      </BrutalCard>

      {/* Held stock */}
      <BrutalCard className="p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50/60 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package size={16} weight="bold" className="text-[#8a6a1c]" />
            <h2 className="text-sm font-semibold tracking-wide uppercase text-zinc-700">{t("portal.held_stock")}</h2>
          </div>
          <span className="text-xs font-mono text-zinc-400" data-testid="portal-held-units">
            {t("portal.units", { count: data.held_units })}
          </span>
        </div>
        {data.held.length === 0 ? (
          <p className="px-5 py-4 text-sm text-zinc-500" data-testid="portal-held-empty">
            {t("portal.no_stock")}
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="portal-held-list">
            <tbody>
              {data.held.map((p, i) => (
                <tr key={i} className="border-b border-zinc-50 last:border-0">
                  <td className="px-5 py-2.5 font-medium text-zinc-900">{p.product_name}</td>
                  <td className="px-5 py-2.5 text-right font-mono text-zinc-700 whitespace-nowrap">
                    {p.quantity} {p.unit}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </BrutalCard>

      <p className="text-center text-[11px] text-zinc-400 font-mono tracking-wider uppercase pb-6">
        {t("portal.footer")}
      </p>
    </div>
  );
}
